import * as React from "react";
import { Loader2, History, RotateCcw } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ScrollArea,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { useRestoreVersion, useTemplateVersions } from "@/lib/emailTemplates/api";
import type { TemplateVersion } from "@/lib/emailTemplates/types";

interface VersionHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateKey: string;
  tenantSlug: string | null;
  /** Notify parent when a restore lands so it can refresh its body buffer. */
  onRestored?: () => void;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function VersionHistoryDialog({
  open,
  onOpenChange,
  templateKey,
  tenantSlug,
  onRestored,
}: VersionHistoryDialogProps) {
  const versions = useTemplateVersions(templateKey, tenantSlug);
  const restore = useRestoreVersion(templateKey, tenantSlug);
  const [selected, setSelected] = React.useState<TemplateVersion | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setSelected(null);
      setError(null);
      restore.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onRestore = async (v: TemplateVersion) => {
    try {
      setError(null);
      await restore.mutateAsync({
        version: v.version,
        notes: `Restored v${v.version} via UI`,
      });
      onRestored?.();
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not restore.",
      );
    }
  };

  const items = versions.data?.items ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="size-4" aria-hidden="true" />
            Version history
          </DialogTitle>
          <DialogDescription>
            {tenantSlug
              ? `Last ${items.length} saves for the ${tenantSlug} override.`
              : `Last ${items.length} saves of the global template.`}
          </DialogDescription>
        </DialogHeader>

        {versions.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="text-muted-foreground size-5 animate-spin" aria-hidden="true" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground py-12 text-center text-sm">
            No prior versions yet — every save creates one.
          </p>
        ) : (
          <ScrollArea className="h-[360px] rounded-md border">
            <ul className="divide-y">
              {items.map((v) => {
                const isOpen = selected?.version === v.version;
                return (
                  <li key={v.version} className="px-3 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 text-sm font-medium">
                          <span>v{v.version}</span>
                          <span className="text-muted-foreground text-xs">
                            {formatDate(v.saved_at)}
                          </span>
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          by {v.saved_by_email}
                          {v.notes ? ` — ${v.notes}` : ""}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelected(isOpen ? null : v)}
                        >
                          {isOpen ? "Hide" : "Inspect"}
                        </Button>
                        <Button size="sm" onClick={() => onRestore(v)} disabled={restore.isPending}>
                          {restore.isPending ? (
                            <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                          ) : (
                            <RotateCcw className="size-3.5" aria-hidden="true" />
                          )}
                          Restore
                        </Button>
                      </div>
                    </div>
                    {isOpen && (
                      <div className="bg-muted/30 mt-3 grid gap-2 rounded-md border p-3 text-xs">
                        <div>
                          <span className="font-medium">Subject:</span> {v.subject}
                        </div>
                        <details>
                          <summary className="text-muted-foreground hover:text-foreground cursor-pointer">
                            HTML body ({v.html_body.length} chars)
                          </summary>
                          <pre className="bg-background mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded p-2 font-mono text-[11px]">
                            {v.html_body}
                          </pre>
                        </details>
                        <details>
                          <summary className="text-muted-foreground hover:text-foreground cursor-pointer">
                            Plain-text body ({v.text_body.length} chars)
                          </summary>
                          <pre className="bg-background mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded p-2 font-mono text-[11px]">
                            {v.text_body}
                          </pre>
                        </details>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}

        {error && (
          <p role="alert" className="text-destructive text-xs">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
